<script setup>
import { ref, nextTick, computed, watch, onMounted, onUnmounted } from 'vue';
import { i18n } from '@/i18n';

const props = defineProps({
  modelValue: {
    type: String,
    default: '',
  },
  scene: {
    type: Object,
  },
  loading: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update:modelValue', 'paste']);

const placeholderTexts = i18n.get('create.dynamicPlaceholders');

const prompt = computed({
  get: () => props.modelValue,
  set: (v) => emit('update:modelValue', v),
});

const textareaRef = ref(null);
const currentPlaceholderIndex = ref(0);
const placeholderText = ref(placeholderTexts[0]);
let placeholderInterval = null;

const updatePlaceholder = () => {
  currentPlaceholderIndex.value = (currentPlaceholderIndex.value + 1) % placeholderTexts.length;
  placeholderText.value = placeholderTexts[currentPlaceholderIndex.value];
};

// 自动调整textarea高度
const adjustTextareaHeight = () => {
  // loading 状态时不调整高度，避免内容清空导致高度跳动
  if (props.loading) return;

  nextTick(() => {
    if (textareaRef.value) {
      textareaRef.value.style.height = 'auto';
      textareaRef.value.style.height = `${Math.min(textareaRef.value.scrollHeight, 400)}px`;
    }
  });
};

// Handle input and update highlights
const handleInput = () => {
  adjustTextareaHeight();
};

// 监听文本变化并实时更新高亮
watch(
  () => props.modelValue,
  () => {
    adjustTextareaHeight();
  },
);

function handlePaste(e) {
  emit('paste', e);
}

onMounted(() => {
  // Start the placeholder rotation
  placeholderInterval = setInterval(() => {
    const textarea = textareaRef.value;
    // Only update placeholder if textarea is empty and not focused
    if (textarea && !textarea.value) {
      updatePlaceholder();
    }
  }, 3000); // Change every 3 seconds

  // Initial height adjustment
  adjustTextareaHeight();
});

onUnmounted(() => {
  if (placeholderInterval) {
    clearInterval(placeholderInterval);
    placeholderInterval = null;
  }
});
</script>

<template>
  <div class="relative">
    <!-- Textarea -->
    <textarea
      ref="textareaRef"
      v-model="prompt"
      class="relative w-full px-5 py-4 text-black/90 placeholder-black/30 custom-scrollbar resize-none focus:outline-none bg-transparent"
      style="min-height: 102px; max-height: 400px; caret-color: #374151"
      :placeholder="loading ? $t(`create.promptLoading`) : placeholderText"
      rows="1"
      @paste="handlePaste"
      @input="handleInput"
    ></textarea>
  </div>
</template>
